import type { AtlasAgentMessage, Prototype } from '@atlas/api-contract';
import { Upload } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { EmptyState, ErrorState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { resourceOwnerId, useCan } from '@/features/auth';
import { useProjectQuery } from '@/features/projects/projects-queries';
import { DeletePrototypeDialog } from '@/features/prototypes/delete-prototype-dialog';
import { EditPrototypeDialog } from '@/features/prototypes/edit-prototype-dialog';
import { useThreadWorkspace } from '@/features/threads/use-thread-workspace';
import { UploadVersionDialog } from '@/features/versions/upload-version-dialog';
import {
  useVersionQuery,
  useVersionsQuery,
} from '@/features/versions/versions-queries';
import { refreshPrototypeAccess } from '@/lib/api/prototype-access';
import { env } from '@/lib/env';

import {
  FRAME_AGENT_TIMEOUT_MS,
  FRAME_LOAD_TIMEOUT_MS,
  INITIAL_FRAME_STATUS,
  isAgentUsable,
  nextFrameStatus,
} from './frame-status';
import { PrototypeFrame } from './prototype-frame';
import { PrototypeRail } from './prototype-rail';
import { buildScreenUrl, buildVersionBaseUrl } from './prototype-url';
import {
  findMatchingScreenPath,
  normaliseScreenPath,
  toNavigateTarget,
} from './screen-paths';
import { useAgentBridge } from './use-agent-bridge';
import { useViewerStore } from './viewer-store';
import { ViewerToolbar } from './viewer-toolbar';
import { resolveSelectedVersion } from './version-selection';
import { resolveFrameWidth } from './width-presets';

export interface ViewerShellProps {
  readonly prototype: Prototype;
  /** The version id from `?version=`, or `undefined` for "the latest". */
  readonly requestedVersionId: string | undefined;
  /** Rewrite `?version=`. `replace` keeps a correction out of history. */
  readonly onVersionChange: (versionId: string, replace: boolean) => void;
  /** Where to go once the prototype has been deleted. */
  readonly onDeleted: () => void;
}

/** Which of the three reused dialogs is open. */
type ViewerDialog = 'edit' | 'upload' | 'delete' | null;

/**
 * How often the viewer re-mints the prototype-access cookie while it is open
 * (spec R7.6). Five minutes inside the cookie's 30-minute life, so a request is
 * never made against an expired one.
 */
const PROTOTYPE_ACCESS_REFRESH_INTERVAL_MS = 25 * 60_000;

/**
 * The viewer: toolbar, framed prototype, and the wiring between them
 * (spec R3.1–R3.7).
 *
 * ============================================================================
 * WHERE EACH FACT LIVES
 * ============================================================================
 * Four owners, and the arrows between them only ever point one way:
 *
 *   URL (`?version=`)  ──▶  store.versionId  ──▶  iframe `src`
 *   agent (`atlas:*`)  ──▶  store.screenPath ──▶  screen dropdown
 *   store.mode         ──▶  `atlas:set-mode` ──▶  agent
 *
 * The screen dropdown is the only control that closes a loop, and it does so
 * deliberately: choosing a screen sends `atlas:navigate` and then *waits* for
 * the agent's `atlas:navigated` to move the store. Setting the store optimistically
 * would show a screen the iframe had not reached, and a navigation the
 * prototype refused would leave the toolbar permanently lying about where the
 * reviewer is.
 */
export const ViewerShell = ({
  prototype,
  requestedVersionId,
  onVersionChange,
  onDeleted,
}: ViewerShellProps) => {
  const [dialog, setDialog] = useState<ViewerDialog>(null);

  const can = useCan();
  const prototypeOwner = resourceOwnerId(prototype);
  /* Upload/edit/delete on a prototype are owner-or-admin (spec matrix). */
  const canUpload = can.uploadVersion(prototypeOwner);
  const canModify = can.modifyOwned(prototypeOwner);

  const versions = useVersionsQuery(prototype.id);

  // The project name for the toolbar's crumb. Usually already cached from the
  // project page the reviewer came through; fetched on a pasted deep link.
  const project = useProjectQuery(prototype.projectId);

  const { version: selectedVersion, requestedMissing } = resolveSelectedVersion(
    versions.data,
    requestedVersionId,
  );
  const selectedVersionId = selectedVersion?.id ?? null;

  const versionDetail = useVersionQuery(selectedVersionId);

  /* ---------------------------------------------------------------------- */
  /* Store                                                                  */
  /* ---------------------------------------------------------------------- */

  const screenPath = useViewerStore((state) => state.screenPath);
  const widthPreset = useViewerStore((state) => state.widthPreset);
  const mode = useViewerStore((state) => state.mode);
  const openVersion = useViewerStore((state) => state.openVersion);
  const setScreenPath = useViewerStore((state) => state.setScreenPath);
  const setScreenKey = useViewerStore((state) => state.setScreenKey);
  const setWidthPreset = useViewerStore((state) => state.setWidthPreset);
  const setMode = useViewerStore((state) => state.setMode);
  const setPendingPin = useViewerStore((state) => state.setPendingPin);
  const railCollapsed = useViewerStore((state) => state.railCollapsed);
  const toggleRail = useViewerStore((state) => state.toggleRail);
  const resetViewer = useViewerStore((state) => state.reset);

  /* The store mirrors the resolved version; it never chooses one. */
  useEffect(() => {
    openVersion(selectedVersionId);
  }, [openVersion, selectedVersionId]);

  /* Leaving the viewer must not leave its state behind for the next one. */
  useEffect(
    () => () => {
      resetViewer();
    },
    [resetViewer],
  );

  /**
   * Keep the address bar honest.
   *
   * Always `replace`: neither filling in the omitted latest version nor
   * correcting a dead one is a navigation the reviewer performed, and pushing
   * either would make the back button bounce them straight onto the link that
   * was already wrong.
   *
   * A dead link is also said out loud once. Silently showing a different
   * version than the one a colleague sent would have the reviewer commenting on
   * the wrong build believing it was the right one.
   */
  const announcedMissingRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedVersionId === null || requestedVersionId === selectedVersionId) {
      return;
    }

    if (
      requestedMissing &&
      requestedVersionId !== undefined &&
      announcedMissingRef.current !== requestedVersionId
    ) {
      announcedMissingRef.current = requestedVersionId;
      toast.info('That version no longer exists. Showing the latest one.');
    }

    onVersionChange(selectedVersionId, true);
  }, [onVersionChange, requestedMissing, requestedVersionId, selectedVersionId]);

  /*
   * Until the agent speaks, the entry file is the best available answer to
   * "which screen is this?" — and for a prototype whose agent never boots it
   * stays the answer, so the dropdown is never blank.
   */
  const entryPath = versionDetail.data?.entryPath ?? null;

  useEffect(() => {
    if (entryPath !== null && screenPath === null) {
      setScreenPath(normaliseScreenPath(entryPath));
    }
  }, [entryPath, screenPath, setScreenPath]);

  /* ---------------------------------------------------------------------- */
  /* Frame + bridge                                                         */
  /* ---------------------------------------------------------------------- */

  const frameRef = useRef<HTMLIFrameElement>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [status, dispatchFrame] = useReducer(
    nextFrameStatus,
    INITIAL_FRAME_STATUS,
  );

  /*
   * The threads feature reads the same stream. It is reached through a ref
   * rather than called directly because the two depend on each other in a
   * circle — `useThreadWorkspace` needs the bridge's senders, and the bridge
   * needs a message handler — and the ref is the same trick `useAgentBridge`
   * uses on its own `onMessage`: assigned during render, read at event time.
   */
  const onThreadMessageRef = useRef<(message: AtlasAgentMessage) => void>(
    () => undefined,
  );

  /*
   * The access-refreshed sender, reached through a ref for the same reason as
   * the thread handler: it is produced by `useAgentBridge` below, and reading it
   * at event time keeps `handleAgentMessage` off the bridge's identity.
   */
  const sendAccessRefreshedRef = useRef<() => void>(() => undefined);

  const handleAgentMessage = useCallback(
    (message: AtlasAgentMessage) => {
      switch (message.type) {
        case 'atlas:ready':
          dispatchFrame('agent-ready');
          setScreenPath(normaliseScreenPath(message.screenPath));
          setScreenKey(message.screenKey);
          break;

        case 'atlas:navigated':
          setScreenPath(normaliseScreenPath(message.screenPath));
          setScreenKey(message.screenKey);
          break;

        case 'atlas:pin-candidate': {
          const { type: _type, ...candidate } = message;
          setPendingPin(candidate);
          break;
        }

        case 'atlas:prototype-access-expired':
          /*
           * An asset 401'd: the signed access has expired mid-session. Refresh
           * it, then acknowledge so the agent retries the failed request once
           * (spec R7.6). Invisible to the user.
           */
          void refreshPrototypeAccess()
            .then(() => {
              sendAccessRefreshedRef.current();
            })
            .catch(() => undefined);
          break;

        case 'atlas:error':
          toast.error(message.message);
          break;

        default:
          break;
      }

      /*
       * After the shell, never instead of it: `atlas:ready` and
       * `atlas:navigated` matter to both halves, and the screen path has to be
       * in the store before the threads feature recomputes which pins belong on
       * it.
       */
      onThreadMessageRef.current(message);
    },
    [setPendingPin, setScreenKey, setScreenPath],
  );

  const {
    ready: agentReady,
    reset: resetBridge,
    setMode: sendMode,
    navigate: sendNavigate,
    renderPins: sendRenderPins,
    highlightPin: sendHighlightPin,
    scrollToPin: sendScrollToPin,
    replay: sendReplay,
    notifyAccessRefreshed: sendAccessRefreshed,
  } = useAgentBridge({ frameRef, onMessage: handleAgentMessage });

  sendAccessRefreshedRef.current = sendAccessRefreshed;

  /* ---------------------------------------------------------------------- */
  /* Prototype access (spec R7.6)                                           */
  /* ---------------------------------------------------------------------- */

  /*
   * The signed-access cookie must be set before the iframe requests its first
   * asset, so the frame's `src` is held back (via `accessReady`) until the first
   * refresh settles, and re-minted on a 25-minute timer for as long as the
   * viewer is open. A failed refresh does not block the frame — the agent's
   * retry-on-401 path (`atlas:prototype-access-expired`) is the safety net.
   */
  const [accessReady, setAccessReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setAccessReady(false);

    void refreshPrototypeAccess()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) {
          setAccessReady(true);
        }
      });

    const interval = globalThis.setInterval(() => {
      void refreshPrototypeAccess().catch(() => undefined);
    }, PROTOTYPE_ACCESS_REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      globalThis.clearInterval(interval);
    };
  }, [prototype.id]);

  const frameSrc =
    selectedVersionId === null
      ? null
      : buildVersionBaseUrl(
          env.VITE_SANDBOX_ORIGIN,
          prototype.id,
          selectedVersionId,
        );

  /*
   * A new document is starting. This runs on the parent-initiated changes only
   * — version switch and explicit retry — because those are the ones where the
   * old agent is gone before the new one exists. A navigation the *prototype*
   * initiates is handled by `atlas:ready` arriving from the new document.
   */
  useEffect(() => {
    if (frameSrc === null) {
      return;
    }

    dispatchFrame('reset');
    resetBridge();
  }, [frameSrc, reloadToken, resetBridge]);

  /* Nothing rendered within the budget: the reviewer is looking at a blank box. */
  useEffect(() => {
    if (status !== 'loading') {
      return undefined;
    }

    const timer = globalThis.setTimeout(() => {
      dispatchFrame('load-timeout');
    }, FRAME_LOAD_TIMEOUT_MS);

    return () => {
      globalThis.clearTimeout(timer);
    };
  }, [status, frameSrc, reloadToken]);

  /* Rendered, but the agent never answered. */
  useEffect(() => {
    if (status !== 'loaded') {
      return undefined;
    }

    const timer = globalThis.setTimeout(() => {
      dispatchFrame('agent-timeout');
    }, FRAME_AGENT_TIMEOUT_MS);

    return () => {
      globalThis.clearTimeout(timer);
    };
  }, [status]);

  /*
   * Re-assert the mode whenever the channel comes up. The agent boots in browse
   * mode and knows nothing about what the reviewer had switched on before the
   * reload, so this is what makes comment mode survive a version switch.
   */
  useEffect(() => {
    if (agentReady) {
      sendMode(mode);
    }
  }, [agentReady, mode, sendMode]);

  const handleFrameLoad = useCallback(() => {
    dispatchFrame('load');
  }, []);

  const handleRetry = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  /* ---------------------------------------------------------------------- */
  /* Threads (spec R5, R6)                                                  */
  /* ---------------------------------------------------------------------- */

  const threadBridge = useMemo(
    () => ({
      navigate: sendNavigate,
      renderPins: sendRenderPins,
      highlightPin: sendHighlightPin,
      scrollToPin: sendScrollToPin,
      replay: sendReplay,
    }),
    [
      sendHighlightPin,
      sendNavigate,
      sendRenderPins,
      sendReplay,
      sendScrollToPin,
    ],
  );

  /*
   * The path the version is served under — `/<prototypeId>/<versionId>/`. A
   * thread records `location.pathname`, so this prefix is on the front of every
   * `screenPath` it stores, and stripping it is what turns a panel row's screen
   * from two UUIDs and a filename into just the filename.
   */
  const basePath = useMemo(() => {
    if (frameSrc === null) {
      return null;
    }

    try {
      return new URL(frameSrc).pathname;
    } catch {
      return null;
    }
  }, [frameSrc]);

  const threadWorkspace = useThreadWorkspace({
    versionId: selectedVersionId,
    agentReady,
    basePath,
    entryPath,
    bridge: threadBridge,
  });

  onThreadMessageRef.current = threadWorkspace.onAgentMessage;

  /* ---------------------------------------------------------------------- */
  /* Toolbar handlers                                                       */
  /* ---------------------------------------------------------------------- */

  const screenPaths = versionDetail.data?.screenPaths ?? [];
  const currentScreenOption = findMatchingScreenPath(
    screenPaths,
    screenPath,
    entryPath,
  );

  const handleSelectScreen = useCallback(
    (nextScreenPath: string) => {
      sendNavigate(toNavigateTarget(nextScreenPath));
    },
    [sendNavigate],
  );

  const handleSelectVersion = useCallback(
    (versionId: string) => {
      onVersionChange(versionId, false);
    },
    [onVersionChange],
  );

  const openUpload = useCallback(() => {
    setDialog('upload');
  }, []);

  const openEdit = useCallback(() => {
    setDialog('edit');
  }, []);

  const openDelete = useCallback(() => {
    setDialog('delete');
  }, []);

  const closeDialog = useCallback((open: boolean) => {
    if (!open) {
      setDialog(null);
    }
  }, []);

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */

  const rawUrl =
    selectedVersionId === null
      ? null
      : buildScreenUrl(
          env.VITE_SANDBOX_ORIGIN,
          prototype.id,
          selectedVersionId,
          screenPath,
        );

  const dialogs = (
    <>
      <EditPrototypeDialog
        prototype={dialog === 'edit' ? prototype : null}
        onOpenChange={closeDialog}
      />
      <UploadVersionDialog
        prototype={dialog === 'upload' ? prototype : null}
        onOpenChange={closeDialog}
      />
      <DeletePrototypeDialog
        prototype={dialog === 'delete' ? prototype : null}
        onOpenChange={closeDialog}
        onDeleted={onDeleted}
      />
    </>
  );

  const renderCanvas = () => {
    if (versions.isError) {
      return (
        <div className="flex h-full items-center justify-center p-6">
          <ErrorState
            className="max-w-lg"
            title="Could not load this prototype's versions"
            error={versions.error}
            onRetry={() => {
              void versions.refetch();
            }}
          />
        </div>
      );
    }

    if (versions.isPending) {
      return (
        <div className="flex h-full justify-center p-4 sm:p-6 lg:p-8">
          <Skeleton
            className="h-full w-full rounded-lg"
            aria-label="Loading the prototype"
          />
        </div>
      );
    }

    /*
     * No versions is not a failure. It is what every prototype looks like for
     * the minutes between someone creating it and someone uploading to it, so
     * it gets an invitation rather than an apology.
     */
    if (selectedVersionId === null || frameSrc === null) {
      return (
        <div className="flex h-full items-center justify-center p-6">
          <EmptyState
            className="max-w-lg bg-background"
            icon={Upload}
            title="Nothing has been uploaded yet"
            description={
              canUpload
                ? 'Upload a ZIP containing index.html and the assets it loads. It becomes version 1, and everything commented on it stays attached to it.'
                : 'Nothing has been uploaded to this prototype yet. Its first version has to be uploaded by a designer or an admin.'
            }
            action={
              canUpload ? (
                <Button onClick={openUpload}>
                  <Upload aria-hidden="true" />
                  Upload the first version
                </Button>
              ) : undefined
            }
          />
        </div>
      );
    }

    /*
     * There is a version, but the access cookie is not yet set. Hold the frame
     * back so its first asset request cannot race the cookie (spec R7.6).
     */
    if (!accessReady) {
      return (
        <div className="flex h-full justify-center p-4 sm:p-6 lg:p-8">
          <Skeleton
            className="h-full w-full rounded-lg"
            aria-label="Preparing the prototype"
          />
        </div>
      );
    }

    if (versionDetail.isError) {
      return (
        <div className="flex h-full items-center justify-center p-6">
          <ErrorState
            className="max-w-lg"
            title="Could not load this version"
            error={versionDetail.error}
            onRetry={() => {
              void versionDetail.refetch();
            }}
          />
        </div>
      );
    }

    return (
      <PrototypeFrame
        src={frameSrc}
        reloadToken={reloadToken}
        width={resolveFrameWidth(widthPreset)}
        title={`${prototype.name}, version ${String(selectedVersion?.versionNumber ?? '')}`}
        frameRef={frameRef}
        status={status}
        onLoad={handleFrameLoad}
        onRetry={handleRetry}
        overlay={threadWorkspace.overlay}
      />
    );
  };

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-viewer-canvas">
      <ViewerToolbar
        prototype={prototype}
        projectName={project.data?.name ?? null}
        versions={versions.data ?? []}
        versionsLoading={versions.isPending}
        selectedVersionId={selectedVersionId}
        onSelectVersion={handleSelectVersion}
        screenPaths={screenPaths}
        currentScreenPath={currentScreenOption}
        onSelectScreen={handleSelectScreen}
        widthPreset={widthPreset}
        onWidthPresetChange={setWidthPreset}
        mode={mode}
        onModeChange={setMode}
        agentUnavailable={!isAgentUsable(status)}
        rawUrl={rawUrl}
        canUpload={canUpload}
        canModify={canModify}
        onEdit={openEdit}
        onUpload={openUpload}
        onDelete={openDelete}
      />

      {/*
        The panel is a sibling of the canvas rather than an overlay on it: it
        takes its 20rem out of the space the prototype is measured in, so a
        reviewer checking a 1440-wide layout can see whether it actually fits
        beside the comments they are reading, and the frame's own centring keeps
        working against whatever is left.
      */}
      <div className="flex min-h-0 flex-1">
        <PrototypeRail
          projectId={prototype.projectId}
          currentPrototypeId={prototype.id}
          collapsed={railCollapsed}
          onToggle={toggleRail}
        />
        <div className="min-h-0 flex-1">{renderCanvas()}</div>
        {threadWorkspace.panel}
      </div>

      {dialogs}
    </div>
  );
};
